import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Award, Building, GraduationCap, Users, Calendar, MapPin, Phone, MessageCircle, ArrowRight, CheckCircle } from "lucide-react";

const TIMELINE = [
  { year: "1995", title: "MBBS", desc: "JN Medical College, Sawangi, Wardha", icon: GraduationCap },
  { year: "1998-2001", title: "MD Psychiatry", desc: "Government Medical College, Amritsar", icon: GraduationCap },
  { year: "2001-2003", title: "Mental Hospital & CMC", desc: "Mental Hospital Amritsar & CMC Ludhiana", icon: Building },
  { year: "2003-2014", title: "Consultant Psychiatrist", desc: "Fortis Hospital, Mohali (11 years)", icon: Building },
  { year: "2014-2023", title: "Director & HOD", desc: "Department of Psychiatry, Fortis Mohali", icon: Award },
  { year: "2023-Present", title: "Private Practice", desc: "Opposite Housefed Society 2, 704 HT Towers, Sector 79, SAS Nagar", icon: Building },
];

const MEMBERSHIPS = [
  "Fellow, Indian Psychiatric Society (FIPS)",
  "Member, American Psychiatric Association (APA)",
  "Member, World Psychiatric Association (WPA)",
  "Certified Sexologist, American College of Sexologists",
  "American Board of Sexologists",
  "Punjab Medical Council Registration No. 29107",
];

export default function AboutPage() {
  return (
    <div>
      {/* Hero */}
      <section className="py-16 sm:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="about-heading">
                About Dr. Ambrish Singal
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-lg">
                PSYCHIATRIST, DRUG DE-ADDICTION SPECIALIST & SEXOLOGIST. One of the most experienced and trusted psychiatrists in the Tricity region with over 28 years of dedicated service in mental healthcare.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "Years Experience", value: "28+" },
                  { label: "Specializations", value: "4" },
                  { label: "As Specialist", value: "24 yrs" },
                  { label: "Affiliations", value: "6+" },
                ].map((stat) => (
                  <div key={stat.label} className="p-4 rounded-xl bg-card border">
                    <span className="font-heading text-2xl font-bold text-primary">{stat.value}</span>
                    <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_neuro-psych-hub/artifacts/w9ksd4ys_Screenshot%202026-04-15%20at%208.47.49%E2%80%AFAM.png"
                alt="Dr. Ambrish Singal"
                className="rounded-2xl shadow-xl w-full max-w-md mx-auto object-cover object-top aspect-[4/5]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Professional Summary */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-6">Professional Summary</h2>
            <div className="prose prose-lg text-muted-foreground space-y-4 text-base leading-relaxed">
              <p>
                Dr. Ambrish Singal is a senior psychiatrist, neuropsychiatrist, certified sexologist, and de-addiction specialist with 28 years of comprehensive clinical experience. He holds MBBS and MD Psychiatry degrees, along with fellowships from the Indian Psychiatric Society (FIPS) and memberships in the American Psychiatric Association and World Psychiatric Association.
              </p>
              <p>
                After completing his MD Psychiatry from Government Medical College, Amritsar, Dr. Singal honed his skills at Mental Hospital Amritsar and the prestigious Christian Medical College (CMC) Ludhiana. He then spent 11 distinguished years at Fortis Hospital Mohali, eventually rising to Director & Head of the Department of Psychiatry, a position he held from 2014 to 2023.
              </p>
              <p>
                Currently, Dr. Singal runs his private practice from 704 HT Towers, Sector 79, Sahibzada Ajit Singh Nagar (Mohali), where he provides comprehensive psychiatric care including drug de-addiction programmes, sexual health consultations, and treatment for the full spectrum of psychiatric conditions. His practice covers neuropsychiatric disorders, drug de-addiction, sexual health, and geriatric psychiatry.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 sm:py-24 bg-secondary/30" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-12">Career Timeline</h2>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-border md:-translate-x-px" />
            <div className="space-y-8 md:space-y-12">
              {TIMELINE.map((item, i) => (
                <div key={item.year} className={`relative flex items-start gap-4 md:gap-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`hidden md:block md:w-1/2 ${i % 2 === 0 ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'}`}>
                    <Card className="inline-block border">
                      <CardContent className="p-5">
                        <span className="text-primary font-heading font-bold text-lg">{item.year}</span>
                        <h3 className="font-heading font-semibold mt-1">{item.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{item.desc}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="absolute left-4 md:left-1/2 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center -translate-x-1/2 z-10">
                    <item.icon className="w-4 h-4" />
                  </div>
                  <div className="md:hidden ml-12">
                    <span className="text-primary font-heading font-bold">{item.year}</span>
                    <h3 className="font-heading font-semibold">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                  <div className="hidden md:block md:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Qualifications & Memberships */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-6">Qualifications</h2>
              <div className="space-y-4">
                {[
                  { deg: "MBBS", inst: "JN Medical College, Sawangi, Wardha (1995)" },
                  { deg: "MD Psychiatry", inst: "Government Medical College, Amritsar (2001)" },
                  { deg: "FIPS", inst: "Fellow, Indian Psychiatric Society" },
                  { deg: "MAPA", inst: "Member, American Psychiatric Association" },
                  { deg: "Certified Sexologist", inst: "American Board & College of Sexologists" },
                ].map((q) => (
                  <div key={q.deg} className="flex gap-3 items-start">
                    <GraduationCap className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-heading font-medium">{q.deg}</h4>
                      <p className="text-sm text-muted-foreground">{q.inst}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-6">Professional Memberships</h2>
              <div className="space-y-3">
                {MEMBERSHIPS.map((m) => (
                  <div key={m} className="flex gap-3 items-start">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <p className="text-sm">{m}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
            Schedule Your Consultation
          </h2>
          <p className="text-base opacity-90 mb-8 max-w-2xl mx-auto">
            Take the first step towards better mental health. Book a confidential appointment with Dr. Ambrish Singal.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+919915096633" data-testid="about-call-btn">
              <Button size="lg" variant="secondary" className="gap-2 rounded-full">
                <Phone className="w-5 h-5" /> Call Now
              </Button>
            </a>
            <Link to="/contact" data-testid="about-book-btn">
              <Button size="lg" variant="outline" className="gap-2 rounded-full border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                Book Appointment <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
